import { GridRenderCellParams } from "@mui/x-data-grid";
import RenderSvg from "../../components/renderSvg/RenderSvg";
import { fetchAndSaveProductDetails, fetchSalesReturnData, getInvoiceDetails, getReturnData, getTaxDetails, setBatchDetailsFromReturns } from "./SalesReturnService";
import { SalesReturnDetail, SalesDetail } from "../../types/types";
import { openPopup } from "../../stateManagement/actions/popupActions";

export const salesReturnColumnsMetaData = {
  columns: [
    {
      headerName: "Return Order No.",
      headerParts: { top: "Return", bottom: "Order No." },
      mappedValue: "returnNumber",
      width: 120,
      headerAlign: "left",
      align: "left",
      noHyperlinkStyle: true,
      truncateText: true,
      cellRenderElement: (params: GridRenderCellParams) => (
        <div 
          style={{ 
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "100%"
          }}
          title={params.row.returnNumber}
        >
          {params.row.returnNumber}
        </div>
      )
    },
    {
      headerName: "Invoice No.",
      headerParts: { top: "Invoice", bottom: "No." },
      mappedValue: "invoiceNumberToDisplay",
      width: 120,
      headerAlign: "left",
      align: "left",
      truncateText: true,
    },
    {
      headerName: "Outlet Name & Code",
      headerParts:{top: "Outlet Name", bottom: "& Code"},
      width: 140,
      mappedValue: "outletCode",
      cellRenderElement: (params: GridRenderCellParams) => (
        <div style={{display:"flex", flexDirection:"column"}}>
          <div style={{overflow: "hidden",textOverflow: "ellipsis", width: "9rem", whiteSpace: "nowrap"}}>
            {params?.row?.outletName}
          </div>
          <div style={{display:"flex", gap:"6px"}}>
            <div>{params?.row?.outletCode}</div>
              {params?.row?.keyAccount && <div style={{display: "flex", alignItems: "center", height: "15px", width: "15px"}}><RenderSvg name='keyAccountIcon' /></div>}
          </div>
        </div>
      )
    },
    {
      headerName: "SalesRep Name & ID",
      headerParts: { top: "SalesRep Name", bottom: "& ID" },
      mappedValue: "salesRepId, salesRepName",
      width: 140,
      headerAlign: "left",
      align: "left",
      truncateText: true
    },
    {
      headerName: "Route Name & ID",
      headerParts: { top: "Route Name ", bottom: "& ID" },
      mappedValue: "routeName, routeCode",
      width: 140,
      headerAlign: "left",
      align: "left",
      truncateText: true
    },
    {
      headerName: "Sold Qty",
      headerParts: { top: "Sold", bottom: "Qty" },
      mappedValue: "soldCaseQty, soldPieceQty, soldOtherQty",
      width: 100,
      headerAlign: "left",
      align: "left",
      qtyDisplay: true,
    },
    {
      headerName: "Return Qty",
      headerParts: { top: "Return", bottom: "Qty" },
      mappedValue: "returnCaseQty, returnPieceQty, returnOtherQty",
      width: 100,
      headerAlign: "left",
      align: "left",
      qtyDisplay: true,
    },
    {
      headerName: "Return Source",
      headerParts: { top: "Return", bottom: "Source" },
      mappedValue: "source",
      width: 90,
      headerAlign: "left",
      align: "left",
      truncateText: true,
      cellRenderElement: (params: GridRenderCellParams) => (
        <div
          style={{
            background: `linear-gradient(to right, ${
              returnSourceStylingMap[params.value]?.gradientColor ?? ""
            }, transparent)`,
            color: returnSourceStylingMap[params.value]?.textColor ?? "",
            padding: "3px 12px 3px 7px",
            borderRadius: "4px",
            minWidth: "70px",
          }}
        >
          {params.value}
        </div>
      )
    },
    {
      headerName: "Review Status",
      headerParts: { top: "Review", bottom: "Status" },
      mappedValue: "status",
      width: 100,
      headerAlign: "left",
      align: "left",
    },
  ],
  Actions: ["view"],
};

export const salesReturnStatusMapping = [
  {
    id: "PENDING",
    dmsLabelColor: "#CC911F",
    dmsLabel: "Pending",
    dmsColor: "#FFF9EF",
  },
  {
    id: "CREDIT NOTE ISSUED",
    dmsLabelColor: "#029847",
    dmsLabel: "Credit Note Issued",
    dmsColor: "#EAFFF4",
  },
  {
    id: "REJECTED",
    dmsLabelColor: "#C44C36",
    dmsLabel: "Rejected",
    dmsColor: "#FFEFF0",
  },
  {
    id: "APPROVED",
    dmsLabelColor: "#17B4A3",
    dmsLabel: "Approved",
    dmsColor: "#F0FBFF",
  },
];

export const reviewStatusFilterMenuItems = [
  {
    id:"ALL",
    label:"All Sale Returns"
  },
  {
    id:"PENDING",
    label:"Pending"
  },
  {
    id:"APPROVED",
    label:"Approved"
  },
  {
    id:"REJECTED",
    label:"Rejected"
  },
  {
    id:"CREDIT NOTE ISSUED",
    label:"Credit Note Issued"
  },
]

export const keyAccountStatusFilterMenuItems = [
  {
    id:"ALL",
    label:"All accounts"
  },
  {
    id:"KEY ACCOUNTS",
    label:"Key accounts"
  },
  {
    id:"NON KEY ACCOUNTS",
    label:"Non-Key accounts"
  }
]

export const returnSourceStylingMap: Record<string, { textColor: string; gradientColor: string }> = {
  "DMS": {
    textColor: "#3EC6A3",
    gradientColor: "#E0FFF7"
  },
  "SFA": {
    textColor: "#00C6B1",
    gradientColor: "#E3FFFC"
  }
};

export const navigateToSalesReturnDetails = async (
  url: string,
  rowData: SalesReturnDetail,
  navigate: any,
  headerLabel: string
) => {
  try {
    const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") || "{}");
    const { otherUnitLabel } = ConfigDictionary;
    if (rowData.invoiceNumber == null) {
      const returnData = await getReturnData(rowData.returnNumber);
      returnData.accountType = rowData.keyAccount ? "Key Account" : "Non-Key Account";
      returnData.outletName = rowData.outletName;
      returnData.status = rowData.status;
      returnData.saleCondition = rowData.saleCondition;
      returnData.totalProducts = `${rowData.returnCaseQty} Cs, ${
        otherUnitLabel ? `${rowData.returnOtherQty} ${otherUnitLabel}, ` : ""
      }${rowData.returnPieceQty} Pc`;
      const {batchDetails} = await setBatchDetailsFromReturns(rowData.returnNumber, returnData);
      await fetchAndSaveProductDetails(returnData.outletCode, false, returnData.loginId);
      navigate("/dashboard/saleReturns/" + url, {
        state: {
          initialBatchDetails:batchDetails,
          returnData,
          headerLabel
        },
      });
    } else {
      const invoiceDetails = await getInvoiceDetails(rowData.invoiceNumber);
      invoiceDetails.accountType = rowData.keyAccount ? "Key Account" : "Non-Key Account";
      invoiceDetails.outletName = rowData.outletName;
      invoiceDetails.status = rowData.status;
      invoiceDetails.saleCondition = rowData.saleCondition;
      invoiceDetails.loadoutNumber = rowData.loadoutNumber ? rowData.loadoutNumber : "-";
      invoiceDetails.vehicleNumber = rowData.vehicleNumber ? rowData.vehicleNumber : "-";
      invoiceDetails.returnDate = rowData.returnDate;
      invoiceDetails.totalProducts = `${rowData.returnCaseQty} Cs, ${
        otherUnitLabel ? `${rowData.returnOtherQty} ${otherUnitLabel}, ` : ""
      }${rowData.returnPieceQty} Pc`;
      invoiceDetails.returnAmount = rowData.returnAmount;
      invoiceDetails.returnNumber = rowData.returnNumber;
      const {batchDetails, returnAmountMap} = await setBatchDetailsFromReturns(rowData.returnNumber);
      invoiceDetails.salesDetails = invoiceDetails.salesDetails?.filter(
        (salesDetail: SalesDetail) => batchDetails[salesDetail.batchCode]
      );
      const taxDetails = await getTaxDetails(
        rowData.outletCode,
        rowData.supplier,
        invoiceDetails.salesDetails.map((salesDetail: SalesDetail) => salesDetail.batchCode)
      );
      navigate("/dashboard/saleReturns/invoice/" + url, {
        state: {
          invoiceDetails,
          initialBatchDetails:batchDetails,
          returnAmountMap,
          taxDetails,
          headerLabel
        },
      });
    }
  } catch (e) {
    openPopup("Error", "Unable to view sale return please try again");
  }
};